import type { Cita, Cliente, Idea, Pago, Proyecto, Tarea } from '../types';
import { db } from './db';
import { inicializarDB } from './migration';

export interface Storage {
  obtenerCitas(): Promise<Cita[]>;
  obtenerCita(id: string): Promise<Cita | undefined>;
  guardarCita(cita: Cita): Promise<void>;
  eliminarCita(id: string): Promise<void>;
  obtenerClientes(): Promise<Cliente[]>;
  guardarCliente(cliente: Cliente): Promise<void>;
  obtenerPagos(): Promise<Pago[]>;
  guardarPago(pago: Pago): Promise<void>;
  eliminarPago(id: string): Promise<void>;
  obtenerTareas(): Promise<Tarea[]>;
  guardarTarea(tarea: Tarea): Promise<void>;
  eliminarTarea(id: string): Promise<void>;
  obtenerProyectos(): Promise<Proyecto[]>;
  guardarProyecto(proyecto: Proyecto): Promise<void>;
  eliminarProyecto(id: string): Promise<void>;
  obtenerIdeas(): Promise<Idea[]>;
  guardarIdea(idea: Idea): Promise<void>;
  eliminarIdea(id: string): Promise<void>;
}

export const storage: Storage = {
  async obtenerCitas() {
    await inicializarDB();
    const todas = await db.citas.toArray();
    return todas
      .filter((c) => c.deletedAt === null)
      .sort(
        (a, b) =>
          new Date(`${a.fecha} ${a.hora}`).getTime() - new Date(`${b.fecha} ${b.hora}`).getTime()
      );
  },

  async obtenerCita(id) {
    await inicializarDB();
    return db.citas.get(id);
  },

  async guardarCita(cita) {
    await inicializarDB();
    await db.citas.put(cita);
  },

  async eliminarCita(id) {
    await inicializarDB();
    const ahora = new Date().toISOString();
    await db.citas.update(id, (c) => {
      c.deletedAt = ahora;
      c.updatedAt = ahora;
    });
  },

  async obtenerClientes() {
    await inicializarDB();
    const todos = await db.clientes.toArray();
    return todos
      .filter((c) => c.deletedAt === null)
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  },

  async guardarCliente(cliente) {
    await inicializarDB();
    await db.clientes.put(cliente);
  },

  async obtenerPagos() {
    await inicializarDB();
    const todos = await db.pagos.toArray();
    return todos
      .filter((p) => p.deletedAt === null)
      .sort((a, b) => a.fechaLimite.localeCompare(b.fechaLimite));
  },

  async guardarPago(pago) {
    await inicializarDB();
    await db.pagos.put(pago);
  },

  async eliminarPago(id) {
    await inicializarDB();
    const ahora = new Date().toISOString();
    await db.pagos.update(id, (p) => {
      p.deletedAt = ahora;
      p.updatedAt = ahora;
    });
  },

  async obtenerTareas() {
    await inicializarDB();
    const todas = await db.tareas.toArray();
    return todas.filter((t) => t.deletedAt === null).sort(ordenarTareas);
  },

  async guardarTarea(tarea) {
    await inicializarDB();
    await db.tareas.put(tarea);
  },

  async eliminarTarea(id) {
    await inicializarDB();
    const ahora = new Date().toISOString();
    await db.tareas.update(id, (t) => {
      t.deletedAt = ahora;
      t.updatedAt = ahora;
    });
  },

  async obtenerProyectos() {
    await inicializarDB();
    const todos = await db.proyectos.toArray();
    return todos
      .filter((p) => p.deletedAt === null)
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  },

  async guardarProyecto(proyecto) {
    await inicializarDB();
    await db.proyectos.put(proyecto);
  },

  // Borrar un proyecto se lleva sus ideas. El servidor hace la misma cascada al
  // recibir el delete del proyecto, por eso no se encola un delete por idea.
  async eliminarProyecto(id) {
    await inicializarDB();
    const ahora = new Date().toISOString();
    await db.transaction('rw', [db.proyectos, db.ideas], async () => {
      await db.ideas
        .where('proyectoId')
        .equals(id)
        .modify((i) => {
          if (i.deletedAt !== null) return;
          i.deletedAt = ahora;
          i.updatedAt = ahora;
        });
      await db.proyectos.update(id, (p) => {
        p.deletedAt = ahora;
        p.updatedAt = ahora;
      });
    });
  },

  async obtenerIdeas() {
    await inicializarDB();
    const todas = await db.ideas.toArray();
    return todas
      .filter((i) => i.deletedAt === null)
      .sort((a, b) => a.creada.localeCompare(b.creada));
  },

  async guardarIdea(idea) {
    await inicializarDB();
    await db.ideas.put(idea);
  },

  async eliminarIdea(id) {
    await inicializarDB();
    const ahora = new Date().toISOString();
    await db.ideas.update(id, (i) => {
      i.deletedAt = ahora;
      i.updatedAt = ahora;
    });
  },
};

// Ordena tareas: las que tienen fecha límite primero (por fecha ascendente),
// luego las sin fecha por orden de creación. El estado hecha/pendiente lo separa
// la UI, aquí solo definimos un orden estable dentro de cada grupo.
function ordenarTareas(a: Tarea, b: Tarea): number {
  if (a.fechaLimite && b.fechaLimite) return a.fechaLimite.localeCompare(b.fechaLimite);
  if (a.fechaLimite) return -1;
  if (b.fechaLimite) return 1;
  return a.creada.localeCompare(b.creada);
}
